import React from 'react';
import { CheckCircle2, Lock, Clock3, ShieldCheck, PauseCircle, Hand } from 'lucide-react';


interface AnyRecord {
  overridden?: boolean;
  readyToAdvance?: boolean;
  blockerCount?: number;
  subStatus?: string;
}

interface StatusSignalProps {
  record: AnyRecord;
  /** Stage keys paused at stage level (from the summary contract). */
  pausedStages?: Set<string>;
  stage?: string;
  compact?: boolean;
}

/**
 * Renders exactly one status signal for an applicant, from server-computed
 * fields only. Priority: Overridden > Paused > Ready to Matriculate >
 * Blocked > Needs decision > On track. Icon + text, never colour alone.
 */
export const StatusSignal: React.FC<StatusSignalProps> = ({ record, pausedStages, stage, compact }) => {
  const base = `inline-flex items-center gap-1.5 rounded-full font-bold whitespace-nowrap ${
    compact ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-2.5 py-1'
  }`;
  const isPaused = (stage && pausedStages?.has(stage)) || false;

  if (record.overridden) {
    return (
      <span className={`${base} bg-[var(--color-override-bg)] text-[var(--color-override)] border border-[var(--color-override-border)]`}>
        <ShieldCheck className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
        Overridden
      </span>
    );
  }
  if (isPaused) {
    return (
      <span className={`${base} bg-slate-100 text-slate-600 border border-slate-200`}>
        <PauseCircle className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
        Paused
      </span>
    );
  }
  if (record.readyToAdvance) {
    return (
      <span className={`${base} bg-emerald-50 text-emerald-700 border border-emerald-200`}>
        <CheckCircle2 className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
        Ready to Matriculate
      </span>
    );
  }
  if ((record.blockerCount || 0) > 0) {
    return (
      <span className={`${base} bg-amber-50 text-amber-800 border border-amber-200`}>
        <Lock className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
        Blocked ({record.blockerCount})
      </span>
    );
  }
  if (String(record.subStatus || '').toLowerCase().includes('awaiting decision')) {
    return (
      <span className={`${base} bg-sky-50 text-sky-800 border border-sky-200`}>
        <Hand className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
        Needs decision
      </span>
    );
  }
  return (
    <span className={`${base} bg-blue-50 text-blue-700 border border-blue-200`}>
      <Clock3 className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
      On track
    </span>
  );
};
